import { DEBOUNCE_INPUT_CHANGE_TIME_SMALL } from '@abb-procure/constants';
import {
  AvailableSupplierRequest,
  SupplierLookupApiService,
  SupplierLookupGroupModel,
  SupplierLookupModel,
  SupplierLookupPaginatedGroupResponseModel,
  SupplierLookupPaginatedResponseModel,
} from '@abb-procure/data';
import { BooleanInput } from '@angular/cdk/coercion';
import {
  Component,
  DestroyRef,
  HostBinding,
  OnInit,
  ViewEncapsulation,
  booleanAttribute,
  effect,
  inject,
  input,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { NgSelectModule } from '@ng-select/ng-select';
import { Subject } from 'rxjs';
import { debounceTime, map, switchMap, tap } from 'rxjs/operators';
import { JoinPipe } from '../../pipes/join.pipe';
import { isRequired } from '../../utilities/form-control.util';
import { Button } from '../button/button.component';
import { NgSelectFormFieldControlDirective } from '../ng-select/ng-select.directive';
import { ShowField } from '../show-field/show-field.component';

@Component({
  selector: 'procure-input-supplier',
  templateUrl: './input-supplier.component.html',
  styleUrl: './input-supplier.component.scss',
  imports: [
    MatFormField,
    MatIcon,
    MatTooltip,
    NgSelectModule,
    NgSelectFormFieldControlDirective,
    ReactiveFormsModule,
    Button,
    ShowField,
    JoinPipe,
    MatLabel,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None,
})
export class InputSupplier implements OnInit {
  @HostBinding('attr.aria-describedby') describedBy: string = '';

  readonly id = input<string | null>(null);
  readonly label = input<string>('');
  readonly hint = input<string>('');
  readonly control = input.required<FormControl>();

  readonly showMode = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  readonly multiple = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  readonly clearable = input<boolean, BooleanInput>(true, {
    transform: booleanAttribute,
  });

  readonly organisation = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  readonly appendTo = input<string>('body');
  readonly bindLabel = input<string>('name');
  readonly groupBy = input<string>('');
  readonly localDivisionIds = input<string[]>();
  readonly mdfId = input<number>();
  readonly mdfCodeIds = input<number[]>();
  readonly registrationStatusIds = input<number[]>();
  readonly countryIds = input<number[]>();
  readonly placeholder = input<string>('');

  readonly suppliers = signal<
    SupplierLookupModel[] | SupplierLookupGroupModel[]
  >([]);

  readonly isLoading = signal(false);
  listInitialized: boolean = false;
  searchValue: string = '';
  dataSourceTotalLength: number = 0;
  pageIndex: number = 1;
  pageSize: number = 20;
  readonly emptyState = 'No suppliers found';

  // Must be public to use in the ng-select.
  typeahead$ = new Subject<string>();

  protected get empty(): boolean {
    const { value } = this.control();
    return value == null || (Array.isArray(value) && value.length === 0);
  }

  protected get isRequired(): boolean {
    return isRequired(this.control());
  }

  protected get value(): string {
    const val = this.control()?.value || [];

    if (!Array.isArray(val)) {
      return val.name;
    }

    return val.map((user) => user.name).join(', ');
  }

  private readonly supplierApiService = inject(SupplierLookupApiService);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    effect(() => {
      this.fetchData();
    });
  }

  ngOnInit(): void {
    this.typeahead$
      .pipe(
        tap((value) => {
          this.searchValue = value;
        }),
        debounceTime(DEBOUNCE_INPUT_CHANGE_TIME_SMALL),
        map((value) => (value != null ? value.trim() : '')),
        tap(() => {
          this.isLoading.set(true);
        }),
        switchMap((filterString) => {
          const requestBase = {
            searchTerm: filterString ?? '',
            pageSize: this.pageSize,
            pageIndex: this.pageIndex,
            orderBy: 'name',
            orderIsAscending: true,
            localDivisionIds: this.localDivisionIds() ?? [],
          };

          return this.organisation()
            ? this.supplierApiService.getSuppliersOrganisation({
                ...requestBase,
                mdfCodeIds: this.mdfCodeIds() ?? [],
                qualificationStatuses: [],
                registrationStatuses: this.registrationStatusIds() ?? [],
                countryIds: this.countryIds() ?? [],
              })
            : this.supplierApiService.getSuppliersBase(
                this.buildBaseRequest(filterString ?? ''),
              );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => this.handleSearchResponse(response));
  }

  async changePage(direction: 'next' | 'prev'): Promise<void> {
    this.isLoading.set(true);
    this.pageIndex += direction === 'next' ? 1 : -1;

    const requestBase = {
      searchTerm: this.searchValue || '',
      pageSize: this.pageSize,
      pageIndex: this.pageIndex,
      orderBy: 'name',
      orderIsAscending: true,
      localDivisionIds: this.localDivisionIds() ?? [],
    };

    const getSuppliers = this.organisation()
      ? this.supplierApiService.getSuppliersOrganisation({
          ...requestBase,
          mdfCodeIds: this.mdfCodeIds() ?? [],
          qualificationStatuses: [],
          registrationStatuses: this.registrationStatusIds() ?? [],
          countryIds: this.countryIds() ?? [],
        })
      : this.supplierApiService.getSuppliersBase(
          this.buildBaseRequest(this.searchValue || ''),
        );

    this.handleSearchResponse(await getSuppliers);
  }

  suppliersCompare: (
    o1: SupplierLookupModel,
    o2: SupplierLookupModel,
  ) => boolean = (o1, o2) => o1.id === o2.id;

  private handleSearchResponse(
    response:
      | SupplierLookupPaginatedResponseModel
      | SupplierLookupPaginatedGroupResponseModel
      | null,
  ): void {
    this.isLoading.set(false);

    this.suppliers.set(response?.items ?? []);
    this.dataSourceTotalLength = response?.totalCount ?? 0;

    this.listInitialized = true;
  }

  private async fetchData(): Promise<void> {
    const requestBase = {
      searchTerm: '',
      pageSize: this.pageSize,
      pageIndex: this.pageIndex,
      orderBy: 'name',
      orderIsAscending: true,
      localDivisionIds: this.localDivisionIds() ?? [],
    };

    const getSuppliers = this.organisation()
      ? this.supplierApiService.getSuppliersOrganisation({
          ...requestBase,
          mdfCodeIds: this.mdfCodeIds() ?? [],
          qualificationStatuses: [],
          registrationStatuses: this.registrationStatusIds() ?? [],
          countryIds: this.countryIds() ?? [],
        })
      : this.supplierApiService.getSuppliersBase(this.buildBaseRequest(''));

    this.handleSearchResponse(await getSuppliers);
  }

  private buildBaseRequest(searchTerm: string): AvailableSupplierRequest {
    const startRow = (this.pageIndex - 1) * this.pageSize;
    const mdfId = this.mdfId() ?? 0;

    return {
      startRow,
      endRow: startRow + this.pageSize,
      sortAscending: true,
      localDivisionIds: this.localDivisionIds() ?? [],
      searchTerm: searchTerm || null,
      mDFs: mdfId ? [mdfId] : [],
    };
  }
}
